<template>
  <div class="flex flex-col gap-6">
    <form class="rounded-md border p-4" @submit.prevent="addSelectedUser">
      <div class="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
        <UserSelect v-model="selectedUser" :label="$t('chat.admin.addUser')" placeholder="Search users by name or email" />

        <Button type="submit" :disabled="isSaving || !selectedUser">
          <UserPlus class="h-4 w-4" />
          {{ $t('chat.admin.addAccess') }}
        </Button>
      </div>
    </form>

    <div class="overflow-hidden rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{{ $t('chat.admin.user') }}</TableHead>
            <TableHead class="text-right">
              {{ $t('chat.admin.access') }}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow v-for="user in users" :key="user.id">
            <TableCell>
              <div class="flex min-w-0 flex-col gap-1">
                <span class="font-medium">{{ user.name }}</span>
                <span class="text-xs text-muted-foreground">{{ user.email }}</span>
              </div>
            </TableCell>
            <TableCell class="text-right">
              <Button type="button" variant="destructive" size="sm" :disabled="isRemoving(user.id) || !user.can_remove_chat_access" @click="removeUserAccess(user)">
                <Trash2 class="h-4 w-4" />
                {{ $t('chat.admin.removeAccess') }}
              </Button>
            </TableCell>
          </TableRow>
          <TableRow v-if="!isLoading && users.length === 0">
            <TableCell colspan="2" class="h-24 text-center text-sm text-muted-foreground">
              {{ $t('chat.admin.noUserAccess') }}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>

    <div class="flex flex-wrap items-center justify-between gap-3">
      <p class="text-sm text-muted-foreground">
        {{ paginationSummary }}
      </p>
      <div class="flex gap-2">
        <Button variant="outline" size="sm" :disabled="isLoading || page <= 1" @click="fetchUsers(page - 1)">
          {{ $t('chat.admin.previous') }}
        </Button>
        <Button variant="outline" size="sm" :disabled="isLoading || page >= lastPage" @click="fetchUsers(page + 1)">
          {{ $t('chat.admin.next') }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup>
import axios from 'axios'
import { computed, onMounted, ref } from 'vue'
import { toast } from 'vue-sonner'
import { Trash2, UserPlus } from 'lucide-vue-next'
import { Button } from '../../../../../../resources/js/components/ui/button/index.js'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../../resources/js/components/ui/table/index.js'
import UserSelect from '@User-manager/components/users/UserSelect.vue'

const selectedUser = ref(null)
const users = ref([])
const isLoading = ref(false)
const isSaving = ref(false)
const removingUserIds = ref(new Set())
const page = ref(1)
const perPage = ref(25)
const total = ref(0)
const lastPage = ref(1)

const paginationSummary = computed(() => {
  if (total.value === 0) {
    return '0 users'
  }

  const start = ((page.value - 1) * perPage.value) + 1
  const end = Math.min(page.value * perPage.value, total.value)

  return `${start}-${end} of ${total.value} users`
})

const addSelectedUser = async () => {
  if (!selectedUser.value) {
    return
  }

  isSaving.value = true

  try {
    await axios.post('/api/v1/chat/admin/users', {
      user_id: selectedUser.value.id,
    })

    selectedUser.value = null
    toast.success('User access added.')
    await fetchUsers(1)
  } catch (error) {
    toast.error(errorMessageFrom(error, 'Unable to add chat access.'))
  } finally {
    isSaving.value = false
  }
}

const removeUserAccess = async (user) => {
  if (!user.can_remove_chat_access) {
    return
  }

  setRemoving(user.id, true)

  try {
    await axios.delete(`/api/v1/chat/admin/users/${user.id}`)
    users.value = users.value.filter((currentUser) => currentUser.id !== user.id)
    total.value = Math.max(total.value - 1, 0)
    toast.success('User access removed.')

    if (users.value.length === 0 && page.value > 1) {
      await fetchUsers(page.value - 1)
    }
  } catch (error) {
    toast.error(errorMessageFrom(error, 'Unable to remove chat access.'))
  } finally {
    setRemoving(user.id, false)
  }
}

const fetchUsers = async (requestedPage = 1) => {
  isLoading.value = true

  try {
    const response = await axios.get('/api/v1/chat/admin/users', {
      params: {
        page: requestedPage,
        per_page: perPage.value,
      },
    })

    users.value = response.data.data || []
    page.value = response.data.meta?.current_page || requestedPage
    perPage.value = response.data.meta?.per_page || perPage.value
    total.value = response.data.meta?.total || users.value.length
    lastPage.value = response.data.meta?.last_page || 1
  } catch (error) {
    toast.error(errorMessageFrom(error, 'Unable to load chat user access.'))
  } finally {
    isLoading.value = false
  }
}

const isRemoving = (userId) => {
  return removingUserIds.value.has(userId)
}

const setRemoving = (userId, removing) => {
  const nextRemovingUserIds = new Set(removingUserIds.value)

  if (removing) {
    nextRemovingUserIds.add(userId)
  } else {
    nextRemovingUserIds.delete(userId)
  }

  removingUserIds.value = nextRemovingUserIds
}

const errorMessageFrom = (error, fallback) => {
  const validationErrors = error?.response?.data?.errors

  if (validationErrors && typeof validationErrors === 'object') {
    const messages = Object.values(validationErrors).flat().filter(Boolean)

    if (messages.length > 0) {
      return messages[0]
    }
  }

  return error?.response?.data?.message || error?.message || fallback
}

onMounted(() => {
  fetchUsers()
})
</script>
